import { useState, useEffect, useCallback, useMemo } from 'react';
import { omitBy, pick } from 'lodash';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAccessorialRequestById } from 'utils/AccessorialRequestWebAPIUtils';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';
import { getAllEquipmentsByEquipId } from 'utils/EquipmentWebAPIUtils';
import { useShipmentById } from 'components/modules/orders/order-rtb/hooks/useShipmentById';
import SessionStore from 'stores/SessionStore';
import Roles from 'constants/UserRoles';
import { showToast } from 'stores/ToastStore';
const whitelist = [
    'carrier_status',
    'shipper_status',
    'shipper_rate',
    'carrier_rate',
    'shipper_unit_qty',
    'carrier_unit_qty',
    'admin_note',
    'shipper_note',
    'carrier_note',
    'unit_name',
    'equipment',
    'equipment_id',
    'message',
    'history',
    '_id',
    'location',
    'begin_datetime',
    'end_datetime',
    'vendor',
    'vendor_name',
    'description',
    'shipper_location',
    'shipper_begin_datetime',
    'shipper_end_datetime',
    'shipper_description',
    'carrier_decline_reason_code',
    'rate_source',
];
export default function useAccessorialRequestData({ shipmentId, accessorialId, }) {
    var _a, _b, _c;
    const queryClient = useQueryClient();
    // Query 1: Shipment (reuse existing hook)
    const { data: fullShipment = null } = useShipmentById(shipmentId, {
        staleTime: 5000,
    });
    // Query 2: Accessorial request
    const accessorialQuery = useQuery({
        queryKey: ['accessorialRequest', accessorialId],
        queryFn: () => getAccessorialRequestById(accessorialId),
        enabled: Boolean(accessorialId),
        staleTime: 5000,
    });
    const accessorial = (_a = accessorialQuery.data) !== null && _a !== void 0 ? _a : null;
    // Query 3: Reference accessorial (depends on accessorial_type)
    const referenceQuery = useQuery({
        queryKey: ['referenceAccessorial', accessorial === null || accessorial === void 0 ? void 0 : accessorial.accessorial_type],
        queryFn: async () => {
            var _a, _b;
            const query = { slug: accessorial.accessorial_type, paginate: false };
            const result = await getAccessorials(query);
            if ((_b = (_a = result === null || result === void 0 ? void 0 : result.entity) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.length) {
                return Object.assign({}, result.entity.items[0]);
            }
            return null;
        },
        enabled: Boolean(accessorial === null || accessorial === void 0 ? void 0 : accessorial.accessorial_type),
        staleTime: 30000,
    });
    const referenceAccessorial = (_b = referenceQuery.data) !== null && _b !== void 0 ? _b : null;
    const accessorialFetched = Boolean(referenceAccessorial);
    // Query 4: Equipments by ID (depends on fullShipment.equipments)
    const equipIds = fullShipment === null || fullShipment === void 0 ? void 0 : fullShipment.equipments;
    const equipmentQuery = useQuery({
        queryKey: ['equipmentsById', equipIds],
        queryFn: async () => {
            var _a, _b;
            const result = await getAllEquipmentsByEquipId(equipIds);
            if ((_a = result === null || result === void 0 ? void 0 : result.entity) === null || _a === void 0 ? void 0 : _a.success) {
                const equipments = (_b = result.entity) === null || _b === void 0 ? void 0 : _b.results;
                if (equipments === null || equipments === void 0 ? void 0 : equipments.length) {
                    return equipments.reduce((acc, { equipment_id, _id }) => {
                        if (!acc[equipment_id]) {
                            acc[equipment_id] = _id;
                        }
                        return acc;
                    }, {});
                }
            }
            return null;
        },
        enabled: Boolean(equipIds === null || equipIds === void 0 ? void 0 : equipIds.length),
        staleTime: 30000,
        retry: false,
    });
    // Surface equipment fetch errors as toasts
    useEffect(() => {
        if (equipmentQuery.error) {
            showToast(equipmentQuery.error.message, 'FAILED_FETCHING_EQUIPMENTS', 'error');
        }
    }, [equipmentQuery.error]);
    // formFields — local editable state, synced from server on fetch
    const [formFields, setFormFields] = useState({});
    useEffect(() => {
        if (!accessorialQuery.data)
            return;
        const newFields = omitBy(pick(accessorialQuery.data, whitelist), (value) => value === undefined);
        setFormFields(newFields);
    }, [accessorialQuery.dataUpdatedAt]);
    // Derive selected equipment when both shipment containers and formFields are available
    const [selectedEquipment, setSelectedEquipment] = useState([]);
    useEffect(() => {
        const containers = (fullShipment === null || fullShipment === void 0 ? void 0 : fullShipment.equipments) || [];
        const equipmentId = formFields.equipment_id;
        if ((containers === null || containers === void 0 ? void 0 : containers.length) &&
            (equipmentId === null || equipmentId === void 0 ? void 0 : equipmentId.length) &&
            containers.includes(equipmentId[0])) {
            setSelectedEquipment(equipmentId[0]);
        }
    }, [fullShipment === null || fullShipment === void 0 ? void 0 : fullShipment.equipments, formFields.equipment_id]);
    // Derived values
    const isAccessorialsAdmin = useMemo(() => SessionStore.isAccessorialsAdmin(), []);
    const isEditable = useMemo(() => {
        if (!accessorial)
            return false;
        if (accessorial.editable || accessorial.editable === undefined)
            return true;
        const userRoles = SessionStore.getUser().roles;
        return userRoles.includes(Roles.FINANCE_ADMIN);
    }, [accessorial]);
    // refresh — wraps invalidateQueries (backward-compatible)
    const refresh = useCallback(() => {
        queryClient.invalidateQueries(['getShipment', shipmentId]);
        queryClient.invalidateQueries(['accessorialRequest', accessorialId]);
        queryClient.invalidateQueries(['referenceAccessorial']);
    }, [queryClient, shipmentId, accessorialId]);
    return {
        fullShipment,
        accessorial,
        referenceAccessorial,
        allEquipmentsById: (_c = equipmentQuery.data) !== null && _c !== void 0 ? _c : undefined,
        accessorialFetched,
        formFields,
        selectedEquipment,
        setFormFields,
        isAccessorialsAdmin,
        isEditable,
        refresh,
    };
}
